/**
 * Shared types for the online Range Officer course.
 *
 * The client bundle ships lessons plus question stems and options. Correct
 * answers live only in functions/src/data/rangeOfficerCourseKey.json and are
 * never imported from src/.
 */

/** Safety block is lessons 1-4; the discipline block is lessons 5-13. */
export type RoCourseSection = 'safety' | 'discipline';

/** Discipline families taught by this course, taken from the 2026 Nationals programme. */
export type RoCourseFamily =
  | 'p25'
  | 'p50'
  | 'tp1'
  | 'mt'
  | 'm5'
  | 'pb25'
  | 'pb50'
  | 'lba'
  | 'hg25'
  | 'hg50';

export const RO_COURSE_FAMILIES: RoCourseFamily[] = [
  'p25',
  'p50',
  'tp1',
  'mt',
  'm5',
  'pb25',
  'pb50',
  'lba',
  'hg25',
  'hg50',
];

export const RO_COURSE_FAMILY_LABELS: Record<RoCourseFamily, string> = {
  p25: '25m Precision',
  p50: '50m Precision',
  tp1: 'Timed & Precision 1',
  mt: 'Multi-Target',
  m5: '1500-M5',
  pb25: '25m Precision Benched',
  pb50: '50m Precision Benched',
  lba: 'Limited Bolt Action (South Africa)',
  hg25: '25m Precision Benched Handgun',
  hg50: '50m Precision Benched Handgun',
};

/** Papers are numbered 1-4; a member sits one of them per attempt. */
export type RoCoursePaper = 1 | 2 | 3 | 4;

export const RO_COURSE_PAPERS: RoCoursePaper[] = [1, 2, 3, 4];

/** Marking thresholds. Kept here so the client and the grader agree. */
export const RO_COURSE_QUESTIONS_PER_PAPER = 40;
export const RO_COURSE_SAFETY_PER_PAPER = 18;
export const RO_COURSE_DISCIPLINE_PER_PAPER = 22;
export const RO_COURSE_PASS_MARK = 28;

/** Set to false to open the course to every logged-in member. */
export const RO_COURSE_ADMIN_PREVIEW = true;

export interface RoCourseOption {
  /** 'a' | 'b' | 'c' | 'd' */
  id: string;
  text: string;
}

export interface RoCourseQuestion {
  /** Stable id, e.g. 'p1-q01'. Matches a key entry. */
  id: string;
  paper: RoCoursePaper;
  section: RoCourseSection;
  /** Lesson this question tests. */
  lessonId: string;
  /** Discipline questions tag the families they cover. */
  families?: RoCourseFamily[];
  stem: string;
  /** Exactly four options. */
  options: RoCourseOption[];
  /** Where the answer comes from, e.g. 'Rule Book 1.13(a)'. */
  reference: string;
}

export interface RoCourseLessonBlock {
  heading: string;
  /** Plain paragraphs. */
  body?: string[];
  /** Bullet points. */
  bullets?: string[];
  /** Optional simple table, rendered as rows under the headers. */
  table?: {
    headers: string[];
    rows: string[][];
  };
}

export interface RoCourseLesson {
  id: string;
  /** 1-13, the order a member must work through. */
  order: number;
  title: string;
  section: RoCourseSection;
  /** One line shown on the course home. */
  summary: string;
  families?: RoCourseFamily[];
  blocks: RoCourseLessonBlock[];
  /** What the Range Officer has to enforce on the range. */
  enforcement: string[];
  /** Rule references behind this lesson. */
  references: string[];
  /** Matching PDF under /rules/, when one exists. */
  pdfHref?: string;
  pdfLabel?: string;
}
